/**
 * 点云资产句柄实现
 * 包装 PointCloudOctree 为符合 PointCloudHandle 接口的句柄
 */

import type * as THREE from 'three'
import type { PointCloudOctree } from '@/plugins/pointCloud/core/PointCloudOctree'
import type {
  PointCloudAssetConfig,
  PointCloudClassificationColorMap,
  PointCloudColorMode,
} from '@/plugins/pointCloud/types'
import { toPointCloudSRGB } from '@/plugins/pointCloud/utils/toPointCloudSRGB'
import type { AssetId, PointAttributes } from '@/shared/types/core'
import type { PointCloudHandle } from '@/shared/types/services'

function normalizeProgress(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(1, Math.max(0, value))
}

type ClassificationStyle = {
  visible: boolean
  color: number[]
}

type ClassificationScheme = Record<string, ClassificationStyle>

const DEFAULT_SEMANTIC_COLOR: [number, number, number, number] = [0.3, 0.6, 0.6, 1]

const COLOR_MODE_ATTRIBUTE_NAMES: Record<PointCloudColorMode, string> = {
  rgba: 'rgba',
  classification: 'classification',
  elevation: 'elevation',
  intensity: 'intensity gradient',
  gps_time: 'gps_time',
  color: 'color',
}

function toClassificationColor(color: THREE.ColorRepresentation): [number, number, number, number] {
  const [r, g, b] = toPointCloudSRGB(color)
  return [r, g, b, 1]
}

function cloneClassificationScheme(classification: ClassificationScheme): ClassificationScheme {
  return Object.fromEntries(
    Object.entries(classification).map(([key, style]) => [
      key,
      { ...style, color: [...style.color] },
    ])
  )
}

/** 将简洁颜色映射展开成不触发 classification 取模回退的完整 LUT。 */
export function createClassificationScheme(
  mapping: PointCloudClassificationColorMap,
  defaultColor?: THREE.ColorRepresentation,
  fallbackColor: readonly number[] = DEFAULT_SEMANTIC_COLOR
): ClassificationScheme {
  const entries = Object.entries(mapping).map(([key, color]) => {
    const classification = Number(key)
    if (!Number.isInteger(classification) || classification < 0 || classification > 255) {
      throw new RangeError(`classification 索引必须是 0~255 的整数，收到: ${key}`)
    }
    return [classification, toClassificationColor(color)] as const
  })

  const resolvedDefault =
    defaultColor === undefined
      ? [
          fallbackColor[0] ?? DEFAULT_SEMANTIC_COLOR[0],
          fallbackColor[1] ?? DEFAULT_SEMANTIC_COLOR[1],
          fallbackColor[2] ?? DEFAULT_SEMANTIC_COLOR[2],
          fallbackColor[3] ?? DEFAULT_SEMANTIC_COLOR[3],
        ]
      : toClassificationColor(defaultColor)
  const classification: ClassificationScheme = {
    DEFAULT: { visible: true, color: [...resolvedDefault] },
  }

  for (let index = 0; index <= 255; index++) {
    classification[index] = { visible: true, color: [...resolvedDefault] }
  }
  for (const [index, color] of entries) {
    classification[index] = { visible: true, color: [...color] }
  }

  return classification
}

/**
 * 八叉树点云句柄
 * 通过私有字段持有 PointCloudOctree，代理其属性访问
 */
export class OctreePointCloudHandle implements PointCloudHandle {
  /** 资产类型标识，固定为 'pointCloud' */
  readonly kind = 'pointCloud' as const
  /** 资产唯一标识 */
  readonly id: AssetId
  /** 点云加载 URL */
  readonly url: string
  /** 用户自定义数据 */
  userData: Record<string, unknown> = {}

  /** 内部八叉树引用，不暴露给外部 */
  #octree: PointCloudOctree
  #onVisualChange?: (reason: string) => void
  #defaultClassificationScheme: ClassificationScheme
  #serviceClassificationScheme: ClassificationScheme | null = null
  #hasClassificationOverride = false

  readonly totalPointCount: number

  constructor(
    id: AssetId,
    octree: PointCloudOctree,
    url: string,
    totalPointCount = 0,
    onVisualChange?: (reason: string) => void
  ) {
    this.id = id
    this.url = url
    this.totalPointCount =
      Number.isFinite(totalPointCount) && totalPointCount > 0 ? totalPointCount : 0
    this.#octree = octree
    this.#onVisualChange = onVisualChange
    this.#defaultClassificationScheme = cloneClassificationScheme(
      octree.material.classification ?? {
        DEFAULT: { visible: true, color: [...DEFAULT_SEMANTIC_COLOR] },
      }
    )
  }

  // ==================== AssetHandle 基础属性 ====================

  /** 资产显示名称，代理到内部八叉树 */
  get name(): string {
    return this.#octree.name
  }

  set name(value: string) {
    this.#octree.name = value
  }

  /** 资产可见性开关 */
  get visible(): boolean {
    return this.#octree.visible
  }

  set visible(value: boolean) {
    const changed = this.#octree.visible !== value
    this.#octree.visible = value
    if (changed) {
      this.#onVisualChange?.('pointcloud.visibility')
    }
  }

  /** 资产局部坐标包围盒，未就绪或为空时返回 null */
  get boundingBox(): THREE.Box3 | null {
    const box = this.#octree.boundingBox
    if (!box || box.isEmpty()) return null
    return box.clone()
  }

  set boundingBox(_value: THREE.Box3 | null) {
    // 八叉树包围盒由几何体决定，不允许外部设置
  }

  /** 资产根对象，挂载到场景树用 */
  get object3D(): THREE.Object3D {
    return this.#octree
  }

  // ==================== PointCloudHandle 扩展属性 ====================

  /** 点属性描述（duck typing 适配 loader 的 PointAttributes 类） */
  get attributes(): PointAttributes {
    return this.#octree.pcoGeometry?.pointAttributes ?? { attributes: [] }
  }

  /**
   * Loader 资产加载生命周期进度（0~1），成功返回的句柄固定为 1。
   * 加载期间的中间值由 `asset.progress` 事件提供，不读取视图 LOD 状态。
   */
  get progress(): number {
    return normalizeProgress(this.#octree.progress, 1)
  }

  /**
   * 当前视图候选 LOD 节点的就绪比例（0~1），不保证单调。
   * 多视口下取当前帧所有可见视口的最小值。
   */
  get viewLodProgress(): number {
    return normalizeProgress(this.#octree.viewLodProgress, 0)
  }

  /** 当前可见节点数 */
  get visibleNodeCount(): number {
    return this.#octree.numVisibleNodes ?? 0
  }

  /** 当前可见点数 */
  get visiblePointCount(): number {
    return this.#octree.numVisiblePoints ?? 0
  }

  /** 坐标投影信息 */
  get projection(): string | null {
    return this.#octree.pcoGeometry?.projection ?? null
  }

  // ==================== Per-asset 材质配置 ====================

  /** 统一切换 RGB、语义、高程、强度、GPS 时间和单色模式。 */
  setColorMode(mode: PointCloudColorMode): void {
    const activeAttributeName = COLOR_MODE_ATTRIBUTE_NAMES[mode]
    if (!activeAttributeName) {
      throw new RangeError(`不支持的点云颜色模式: ${String(mode)}`)
    }

    if (mode === 'classification') {
      const hasClassification = this.attributes.attributes.some(
        (attribute) => attribute.name === 'classification'
      )
      if (!hasClassification) {
        throw new Error(`点云资产 "${this.id}" 缺少 classification 属性，无法开启语义模式`)
      }
    }

    const material = this.#octree.material
    if (material.activeAttributeName === activeAttributeName) return

    material.activeAttributeName = activeAttributeName
    this.#onVisualChange?.('pointcloud.colorMode')
  }

  /** 替换 classification 颜色映射，未配置分类统一使用 defaultColor。 */
  setClassificationColorMap(
    mapping: PointCloudClassificationColorMap,
    defaultColor?: THREE.ColorRepresentation
  ): void {
    const currentDefault = this.#octree.material.classification?.DEFAULT?.color
    const classification = createClassificationScheme(
      mapping,
      defaultColor,
      currentDefault ?? DEFAULT_SEMANTIC_COLOR
    )
    this.#hasClassificationOverride = true
    this.#octree.material.setClassificationScheme(classification)
    this.#onVisualChange?.('pointcloud.classificationColors')
  }

  /** 清除实例级颜色覆盖，重新继承当前服务级颜色映射。 */
  clearClassificationColorMap(): void {
    if (!this.#hasClassificationOverride) return

    this.#hasClassificationOverride = false
    this.#applyInheritedClassificationScheme()
    this.#onVisualChange?.('pointcloud.classificationColors')
  }

  /** 更新服务级分类方案；存在实例级覆盖时只记录、不覆盖实例材质。 */
  applyServiceClassificationScheme(classification: ClassificationScheme | null): void {
    this.#serviceClassificationScheme = classification
      ? cloneClassificationScheme(classification)
      : null
    if (!this.#hasClassificationOverride) {
      this.#applyInheritedClassificationScheme()
    }
  }

  /**
   * 设置单资产材质配置，仅更新传入的属性。
   *
   * @param config - 待更新的配置项，未提供字段保持原值
   */
  setAssetConfig(config: Partial<PointCloudAssetConfig>): void {
    const mat = this.#octree.material
    if (!mat) return

    if (config.size !== undefined) mat.size = config.size
    if (config.minSize !== undefined) mat.minSize = config.minSize
    if (config.opacity !== undefined) mat.opacity = config.opacity
    if (config.pointSizeType !== undefined) mat.pointSizeType = config.pointSizeType
    if (config.shape !== undefined) mat.shape = config.shape
    if (config.activeAttributeName !== undefined)
      mat.activeAttributeName = config.activeAttributeName
    if (config.gradient !== undefined) mat.gradient = config.gradient
    if (config.intensityRange !== undefined) mat.intensityRange = config.intensityRange
    if (config.elevationRange !== undefined) mat.elevationRange = config.elevationRange
    if (config.color !== undefined) mat.color = config.color
    if (config.weightRGB !== undefined) mat.weightRGB = config.weightRGB
    if (config.weightIntensity !== undefined) mat.weightIntensity = config.weightIntensity
    if (config.weightElevation !== undefined) mat.weightElevation = config.weightElevation
    if (config.weightClassification !== undefined)
      mat.weightClassification = config.weightClassification
    if (config.weightReturnNumber !== undefined) mat.weightReturnNumber = config.weightReturnNumber
    if (config.weightSourceID !== undefined) mat.weightSourceID = config.weightSourceID

    if (config.rgbGamma !== undefined) mat.rgbGamma = config.rgbGamma
    if (config.rgbBrightness !== undefined) mat.rgbBrightness = config.rgbBrightness
    if (config.rgbContrast !== undefined) mat.rgbContrast = config.rgbContrast
    if (config.intensityGamma !== undefined) mat.intensityGamma = config.intensityGamma
    if (config.intensityBrightness !== undefined)
      mat.intensityBrightness = config.intensityBrightness
    if (config.intensityContrast !== undefined) mat.intensityContrast = config.intensityContrast
    if (config.extraGamma !== undefined) mat.extraGamma = config.extraGamma
    if (config.extraBrightness !== undefined) mat.extraBrightness = config.extraBrightness
    if (config.extraContrast !== undefined) mat.extraContrast = config.extraContrast
    if (config.extraRange !== undefined) mat.extraRange = config.extraRange
    if (config.activeSamplingRange !== undefined)
      mat.activeSamplingRange = config.activeSamplingRange
    if (config.maxSize !== undefined) mat.maxSize = config.maxSize
    if (config.weighted !== undefined) mat.weighted = config.weighted
    if (config.matcap !== undefined) mat.matcap = config.matcap
    if (config.classification !== undefined) {
      this.#hasClassificationOverride = true
      mat.setClassificationScheme(config.classification)
    }
    if (config.useOrthographicCamera !== undefined)
      mat.useOrthographicCamera = config.useOrthographicCamera
    if (config.useClipBox !== undefined) mat.useClipBox = config.useClipBox
    this.#onVisualChange?.('pointcloud.assetConfig')
  }

  /**
   * 获取当前单资产材质配置快照。
   *
   * @returns 完整的材质配置，反映当前时刻材质状态
   */
  getAssetConfig(): PointCloudAssetConfig {
    const mat = this.#octree.material
    return {
      size: mat.size,
      minSize: mat.minSize,
      opacity: mat.opacity,
      pointSizeType: mat.pointSizeType,
      shape: mat.shape,
      activeAttributeName: mat.activeAttributeName,
      gradient: mat.gradient,
      intensityRange: mat.intensityRange,
      elevationRange: mat.elevationRange,
      color: mat.color,
      weightRGB: mat.weightRGB,
      weightIntensity: mat.weightIntensity,
      weightElevation: mat.weightElevation,
      weightClassification: mat.weightClassification,
      weightReturnNumber: mat.weightReturnNumber,
      weightSourceID: mat.weightSourceID,

      rgbGamma: mat.rgbGamma,
      rgbBrightness: mat.rgbBrightness,
      rgbContrast: mat.rgbContrast,
      intensityGamma: mat.intensityGamma,
      intensityBrightness: mat.intensityBrightness,
      intensityContrast: mat.intensityContrast,
      extraGamma: mat.extraGamma,
      extraBrightness: mat.extraBrightness,
      extraContrast: mat.extraContrast,
      extraRange: mat.extraRange,
      activeSamplingRange: mat.activeSamplingRange,
      maxSize: mat.maxSize,
      weighted: mat.weighted,
      matcap: mat.matcap,
      classification: mat.classification,
      useOrthographicCamera: mat.useOrthographicCamera,
      useClipBox: mat.useClipBox,
    }
  }

  /** 将服务级方案或材质初始方案应用到当前实例。 */
  #applyInheritedClassificationScheme(): void {
    const classification = this.#serviceClassificationScheme ?? this.#defaultClassificationScheme
    this.#octree.material.setClassificationScheme(cloneClassificationScheme(classification))
  }
}
